import type { PdfRequest, PdfResponse } from './protocol';
import { PdfSession } from './session';
import {
  ENGINE_UNAVAILABLE,
  PdfEngineError,
  type PagePlan,
  type PdfErrorCode,
  type PdfInfo,
} from './types';

type PdfSuccess = Extract<PdfResponse, { ok: true }>;

export interface OpenedPdf {
  /** The id to use in a `PagePlan`. */
  readonly id: number;
  readonly name: string;
  readonly bytes: number;
  readonly info: PdfInfo;
}

/**
 * The PDF tool's engine: open files, then build new documents from their pages. Everything runs
 * on the user's device, in a Web Worker where available (on the page otherwise), so a large merge
 * does not freeze the screen. The PDF library is loaded on first use, in the Worker or by the
 * fallback, never in the main bundle.
 *
 * Memory: each file is read once and transferred to the Worker (no copy); the page keeps only the
 * `Blob` (for a `File`, the browser keeps it on disk), so the engine can reopen the files on the
 * page if the Worker fails. Results come back as transferred buffers wrapped in Blobs.
 */
export interface PdfEngine {
  /** Rejects with `PdfEngineError` ('password-required', 'not-a-pdf', 'failed', 'closed'). */
  open(file: Blob, name?: string): Promise<OpenedPdf>;
  assemble(plan: readonly PagePlan[]): Promise<Blob>;
  /** One PDF per zero-based inclusive range of the file's pages. */
  split(source: number, ranges: readonly (readonly [number, number])[]): Promise<Blob[]>;
  close(source: number): void;
  dispose(): void;
}

export function createPdfEngine(): PdfEngine {
  return typeof Worker !== 'undefined' ? new WorkerPdfEngine() : new InlinePdfEngine();
}

const PDF = 'application/pdf';

function asPdf(bytes: Uint8Array | ArrayBuffer): Blob {
  return new Blob([bytes as BlobPart], { type: PDF });
}

/** The session on the page's main thread. */
class InlinePdfEngine implements PdfEngine {
  private readonly session = new PdfSession();
  private readonly infos = new Map<number, PdfInfo>();
  /** Files that could not be reopened after a Worker failure, with the reason. */
  private readonly reopenErrors = new Map<number, Error>();
  private nextId = 1;

  async open(file: Blob, name = ''): Promise<OpenedPdf> {
    const id = this.nextId++;
    return { id, name, bytes: file.size, info: await this.reopen(id, file) };
  }

  /** Opens a file under a given id (used after a Worker failure). */
  async reopen(id: number, file: Blob): Promise<PdfInfo> {
    this.nextId = Math.max(this.nextId, id + 1);
    try {
      const info = await this.session.open(id, await file.arrayBuffer());
      this.infos.set(id, info);
      this.reopenErrors.delete(id);
      return info;
    } catch (error) {
      this.reopenErrors.set(id, error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  infoOf(id: number): PdfInfo | undefined {
    return this.infos.get(id);
  }

  async assemble(plan: readonly PagePlan[]): Promise<Blob> {
    for (const step of plan) this.throwIfLost(step.source);
    return asPdf(await this.session.assemble(plan));
  }

  async split(source: number, ranges: readonly (readonly [number, number])[]): Promise<Blob[]> {
    this.throwIfLost(source);
    return (await this.session.split(source, ranges)).map(asPdf);
  }

  close(source: number): void {
    this.session.close(source);
    this.infos.delete(source);
    this.reopenErrors.delete(source);
  }

  dispose(): void {
    for (const id of [...this.infos.keys()]) this.close(id);
  }

  private throwIfLost(source: number): void {
    const error = this.reopenErrors.get(source);
    if (error) throw error;
  }
}

/** A request with no answer for this long means the Worker died silently (e.g. out of memory). */
const WORKER_SILENCE_MS = 5 * 60 * 1000;

/**
 * The session in a module Worker. If the Worker cannot run — it fails to start, cannot load the
 * PDF library, cannot pass a message, or falls silent — the engine reopens the files on the page
 * and carries on there. An error the Worker reports for one file or request (a password, an
 * unreadable file) is that request's answer, not a Worker failure.
 */
class WorkerPdfEngine implements PdfEngine {
  private worker: Worker | null;
  private fallback: Promise<InlinePdfEngine> | null = null;
  private disposed = false;
  private readonly files = new Map<number, Blob>();
  private nextSource = 1;
  private nextId = 1;
  private readonly pending = new Map<
    number,
    { resolve: (r: PdfSuccess | null) => void; reject: (e: Error) => void; timer: number }
  >();

  constructor() {
    this.worker = new Worker(new URL('./pdf-engine.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent<PdfResponse>) => {
      const response = event.data;
      if (!response.ok && response.code === ENGINE_UNAVAILABLE) {
        void this.failOver();
        return;
      }
      const entry = this.take(response.id);
      if (!entry) return;
      if (response.ok) entry.resolve(response);
      else entry.reject(new PdfEngineError(response.code as PdfErrorCode, response.message));
    };
    this.worker.onerror = () => void this.failOver();
    this.worker.onmessageerror = () => void this.failOver();
  }

  async open(file: Blob, name = ''): Promise<OpenedPdf> {
    this.throwIfDisposed();
    const id = this.nextSource++;
    this.files.set(id, file);
    try {
      const bytes = await file.arrayBuffer();
      const response = await this.call(
        (rid) => ({ id: rid, kind: 'open', source: id, bytes }),
        [bytes],
      );
      if (response?.info) return { id, name, bytes: file.size, info: response.info };
      // The Worker failed: the failover may already have reopened this file.
      const inline = await this.fallbackEngine();
      const info = inline.infoOf(id) ?? (await inline.reopen(id, file));
      return { id, name, bytes: file.size, info };
    } catch (error) {
      this.files.delete(id);
      throw error;
    }
  }

  async assemble(plan: readonly PagePlan[]): Promise<Blob> {
    this.throwIfDisposed();
    const response = await this.call((rid) => ({ id: rid, kind: 'assemble', plan }));
    if (response?.pdfs?.[0]) return asPdf(response.pdfs[0]);
    return (await this.fallbackEngine()).assemble(plan);
  }

  async split(source: number, ranges: readonly (readonly [number, number])[]): Promise<Blob[]> {
    this.throwIfDisposed();
    const response = await this.call((rid) => ({ id: rid, kind: 'split', source, ranges }));
    if (response?.pdfs) return response.pdfs.map(asPdf);
    return (await this.fallbackEngine()).split(source, ranges);
  }

  close(source: number): void {
    if (this.disposed) return;
    this.files.delete(source);
    void this.call((rid) => ({ id: rid, kind: 'close', source })).catch(() => undefined);
    void this.fallback?.then((inline) => inline.close(source));
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.worker?.terminate();
    this.worker = null;
    this.files.clear();
    void this.fallback?.then((inline) => inline.dispose());
    for (const id of [...this.pending.keys()]) {
      this.take(id)?.reject(new PdfEngineError('closed', 'The PDF engine was closed'));
    }
  }

  /**
   * Resolves with the response, or with null when the Worker has gone and the caller must use the
   * page; rejects with the request's own error.
   */
  private call(
    build: (id: number) => PdfRequest,
    transfer: Transferable[] = [],
  ): Promise<PdfSuccess | null> {
    if (this.disposed) {
      return Promise.reject(new PdfEngineError('closed', 'The PDF engine was closed'));
    }
    const worker = this.worker;
    if (!worker) return Promise.resolve(null);
    const id = this.nextId++;
    return new Promise<PdfSuccess | null>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        // Silent for too long: this request fails, and the Worker is treated as dead.
        this.take(id)?.reject(new PdfEngineError('failed', 'The PDF engine stopped answering'));
        void this.failOver();
      }, WORKER_SILENCE_MS);
      this.pending.set(id, { resolve, reject, timer });
      try {
        worker.postMessage(build(id), transfer);
      } catch (error) {
        this.take(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private take(id: number) {
    const entry = this.pending.get(id);
    if (!entry) return undefined;
    this.pending.delete(id);
    window.clearTimeout(entry.timer);
    return entry;
  }

  private throwIfDisposed(): void {
    if (this.disposed) throw new PdfEngineError('closed', 'The PDF engine was closed');
  }

  private fallbackEngine(): Promise<InlinePdfEngine> {
    this.throwIfDisposed();
    return this.failOver();
  }

  /** Stops the Worker, moves the waiting requests to the page and reopens every file there, once. */
  private failOver(): Promise<InlinePdfEngine> {
    this.fallback ??= (async () => {
      this.worker?.terminate();
      this.worker = null;
      for (const id of [...this.pending.keys()]) this.take(id)?.resolve(null);
      const inline = new InlinePdfEngine();
      for (const [id, file] of this.files) {
        // A file that cannot be reopened keeps its error, reported on its next use.
        await inline.reopen(id, file).catch(() => undefined);
      }
      return inline;
    })();
    return this.fallback;
  }
}
